/**
 * feat-227 — data-driven color derivation for the Pairing Challenge card.
 *
 * WHY THIS FILE EXISTS: request §5 is explicit that "colors in the preview must
 * come from the same data-driven `bar_color`, NOT hardcoded hex — a preview that
 * hardcodes what the app derives will drift the first time a bar color changes."
 * There is no test that compares this preview to the phone, so the drift would be
 * silent. Every color the card paints per-team is derived here from the single
 * authored `bar_color`.
 *
 * THE RULES ARE MEASURED, NOT GUESSED. Each one below was read off
 * `.team/features/feat-227-.../target-mockup.png` pixel-by-pixel on 2026-08-08 and
 * the measurement is recorded beside it, so a future reader can re-check the number
 * instead of trusting the comment. The same rules were sent to the Flutter slice for
 * agreement before this was written — AC9 puts the two renders side by side, and two
 * independent guesses at "a pale tint of the team color" would not have matched.
 */

// ── The fallback that Trap 2 is about ─────────────────────────────────────────
/**
 * Fallback when `bar_color` is absent or not `#RRGGBB`. Mirrors the Flutter
 * `_parseHex` fallback.
 *
 * HISTORY — READ BEFORE TOUCHING: this value used to live in `PairingCardPreview`'s
 * palette as `textMuted: '#B0B6BF', // pace line + bar_color fallback`. It was
 * DUAL-ROLE. feat-227 deletes the dashed pace line, and deleting the constant along
 * with it would have silently taken the `bar_color` fallback with it — in a component
 * whose entire job is to show authored colors faithfully. Only the pace-line role is
 * retired. The fallback lives on here, under a name that can only mean one thing.
 */
export const BAR_COLOR_FALLBACK = '#B0B6BF';

// ── Tunables ──────────────────────────────────────────────────────────────────

/**
 * Track (unfilled remainder) = `bar_color` composited over white at this alpha.
 *
 * MEASURED: the mockup's red track is #FDEBEA. Compositing its red #E73332 over
 * white at α=0.10 gives (252.6, 234.6, 234.5) → #FDEBEA. Exact. The gold track
 * #FDF7E5 predicts to (250.7, 245.4, 231.7) — within 3/255.
 */
export const TRACK_TINT_ALPHA = 0.1;

/**
 * One "darken step" multiplies HSL lightness by this factor, leaving hue and
 * saturation alone.
 *
 * MEASURED: the same factor produces the dark end of BOTH rows' gradients —
 * red #E73332 (L 0.551) → #B91B1C (L 0.416), ×0.755; gold #D49F16 (L 0.459) →
 * #A17C0F (L 0.345), ×0.752.
 */
export const DARKEN_LIGHTNESS_FACTOR = 0.75;

/**
 * Minimum contrast every piece of `bar_color`-derived TEXT must reach against the
 * (white, per D6) card surface.
 *
 * RULED by design-lead 2026-08-08 — `worktrees/feat-227-…/design/
 * feat-227-contrast-ruling.md`; token `.team/design/tokens/TOKENS.md §1l`. This is
 * no longer provisional.
 *
 * 4.5, not the 3.0 large-text floor, because the smallest `bar_color` text on the
 * card is the 15 px bold team name. Bold below 18.66 px is not "large text", so the
 * whole text path is held to the stricter floor rather than splitting it per element
 * and letting the name ship unreviewed — which is the gap request §8 had, since it
 * discussed only the numeral.
 */
export const TEAM_TEXT_CONTRAST_FLOOR = 4.5;

/**
 * The luminance a color must be at or below to clear the floor against white.
 * `contrast = 1.05 / (L + 0.05) >= floor` ⇔ `L <= 1.05 / floor − 0.05`.
 */
const TEXT_TARGET_LUMINANCE = 1.05 / TEAM_TEXT_CONTRAST_FLOOR - 0.05;

/** The card surface these contrasts are computed against (D6: white). */
const SURFACE = { r: 255, g: 255, b: 255 };

// ── Color primitives ──────────────────────────────────────────────────────────

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

function parseHex(hex: string): Rgb {
  const h = hex.trim();
  return {
    r: parseInt(h.slice(1, 3), 16),
    g: parseInt(h.slice(3, 5), 16),
    b: parseInt(h.slice(5, 7), 16),
  };
}

const clamp255 = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));

function toHex({ r, g, b }: Rgb): string {
  const part = (n: number): string => clamp255(n).toString(16).padStart(2, '0').toUpperCase();
  return `#${part(r)}${part(g)}${part(b)}`;
}

/**
 * `#RRGGBB` validity check — mirrors the Flutter `_parseHex` fallback. Everything
 * else in this module assumes it has already been through here.
 */
export function safeBarColor(hex: string | undefined | null): string {
  if (hex && HEX_RE.test(hex.trim())) return hex.trim();
  return BAR_COLOR_FALLBACK;
}

function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return { h, s, l };
}

function hslToRgb({ h, s, l }: { h: number; s: number; l: number }): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let rgb: [number, number, number];
  if (hp < 1) rgb = [c, x, 0];
  else if (hp < 2) rgb = [x, c, 0];
  else if (hp < 3) rgb = [0, c, x];
  else if (hp < 4) rgb = [0, x, c];
  else if (hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  const m = l - c / 2;
  return { r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 };
}

function srgbToLinear(channel: number): number {
  const s = channel / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Inverse of `srgbToLinear`; returns a 0–1 sRGB value, not an 8-bit channel. */
function linearToSrgb(linear: number): number {
  return linear <= 0.0031308 ? linear * 12.92 : 1.055 * Math.pow(linear, 1 / 2.4) - 0.055;
}

function relativeLuminance(c: Rgb): number {
  return 0.2126 * srgbToLinear(c.r) + 0.7152 * srgbToLinear(c.g) + 0.0722 * srgbToLinear(c.b);
}

/** WCAG contrast ratio of `hex` against the (white) card surface. */
export function contrastOnSurface(hex: string): number {
  const a = relativeLuminance(parseHex(safeBarColor(hex)));
  const b = relativeLuminance(SURFACE);
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

/** `hex` composited over the white card surface at `alpha`. */
export function compositeOnSurface(hex: string, alpha: number): string {
  const c = parseHex(safeBarColor(hex));
  return toHex({
    r: SURFACE.r + alpha * (c.r - SURFACE.r),
    g: SURFACE.g + alpha * (c.g - SURFACE.g),
    b: SURFACE.b + alpha * (c.b - SURFACE.b),
  });
}

/** `hex` with HSL lightness multiplied by `DARKEN_LIGHTNESS_FACTOR`, `steps` times. */
export function darken(hex: string, steps = 1): string {
  const hsl = rgbToHsl(parseHex(safeBarColor(hex)));
  for (let i = 0; i < steps; i += 1) hsl.l *= DARKEN_LIGHTNESS_FACTOR;
  return toHex(hslToRgb(hsl));
}

// ── The four derived card colors ──────────────────────────────────────────────

/**
 * The color dot beside the team name. Raw `bar_color`, never darkened — it carries no
 * text, so it has no contrast floor to meet and stays exactly on-brand.
 * (Mockup: 55 solid px of its own #E73332. That is the MOCKUP's red, not the seeded
 * #C0122B — the two palettes differ; see the header of the test suite.)
 */
export function barDotColor(hex: string | undefined | null): string {
  return safeBarColor(hex);
}

/** The unfilled remainder of the track (D7 — replaces the flat `#E5E7EB` grey). */
export function barTrackTint(hex: string | undefined | null): string {
  return compositeOnSurface(safeBarColor(hex), TRACK_TINT_ALPHA);
}

/**
 * The gradient fill (§4.3 item 3), as a CSS `background` value.
 *
 * NOTE THE SPAN: the gradient runs across the FILL, not across the track — so it
 * reaches exactly `bar_color` at the fill head whatever the percent is.
 * MEASURED: the mockup's red fill ramps #B91B1C → #DF3030 between x=120 and x=480;
 * extrapolating that ramp to its own 64% fill head lands on (230.5, 52.1, 52.0),
 * i.e. the dot color #E73332. Gold ramps #A17C0F → #CB9A15 and extrapolates to
 * (211, 160, 22) at its 71% head, i.e. its dot color #D3A016. Two different fill
 * lengths, both landing on `bar_color` at their own head. Had the gradient spanned
 * the whole track, red would have reached only 64% of the ramp (#D63030). It does
 * not. Apply this to the fill element, whose width IS the fill.
 */
export function barFillGradient(hex: string | undefined | null): string {
  const base = safeBarColor(hex);
  return `linear-gradient(to right, ${darken(base, 1)} 0%, ${base} 100%)`;
}

/**
 * The ink for ALL `bar_color`-derived text: the percent numeral, its `%` suffix, and
 * the team name (D5). Scales the color down in LINEAR light until it sits exactly at
 * `TEAM_TEXT_CONTRAST_FLOOR`, and returns it untouched when it already passes.
 *
 * WHY A CLOSED FORM AND NOT REPEATED DARKENING — this replaced a `×0.75 lightness
 * step until it clears` loop, and the loop was wrong for two independent reasons:
 *
 * 1. **Stepping cannot land ON a floor, only past it.** At 4.5 the seeded gold takes
 *    two steps to #7E5C00 — 6.14:1, a full 1.64 over the floor, and visibly BROWN
 *    rather than gold. The closed form lands #9B6E00 at 4.54:1: compliant and still
 *    recognisably the team's color, which is the entire point of D5.
 * 2. **AC9 photographs this preview beside the Flutter card.** A hand-ported TS
 *    equivalent of Dart's `HSLColor` agrees with it *mostly*. Four branchless
 *    arithmetic ops agree *exactly*, and both stacks pin the same three vectors.
 *
 * Relative luminance is a linear combination of the linear-light channels, so
 * scaling all three by `k` scales luminance by exactly `k` — the target is reachable
 * in one shot with no search.
 *
 * TRUNCATE, DO NOT ROUND, at the 8-bit step — AND THIS IS NOT STYLISTIC. Verified
 * independently by QA and re-derived here on the seeded gold #E0A200:
 *
 *     Math.floor → #9B6E00 → 4.5414:1   ✅ clears the floor
 *     Math.round → #9B6F00 → 4.4992:1   ❌ UNDER the 4.5 it was just solved for
 *
 * One byte on the green channel is the whole margin. Truncation can only darken, so
 * it can never land under the target; rounding can, and does, right here. It is also
 * the single most likely place for the two stacks to drift apart — rounding would put
 * this preview one byte off the Flutter card on two channels of the gold, which would
 * ship as "the gold looks slightly different on the phone": unfalsifiable at review
 * and miserable to chase.
 */
export function barTextColor(hex: string | undefined | null): string {
  const base = safeBarColor(hex);
  const c = parseHex(base);
  const luminance = relativeLuminance(c);
  // NEVER LIGHTEN. A color already past the floor is returned unchanged — that is
  // what keeps the seeded red on-brand at 6.24:1, and it guards against reading this
  // as "normalise every color to 4.5".
  if (luminance <= TEXT_TARGET_LUMINANCE) return base;
  const k = TEXT_TARGET_LUMINANCE / luminance;
  const scale = (channel: number): number =>
    Math.max(0, Math.min(255, Math.floor(linearToSrgb(srgbToLinear(channel) * k) * 255)));
  return toHex({ r: scale(c.r), g: scale(c.g), b: scale(c.b) });
}

/**
 * The `%` suffix beside the numeral — THE SAME INK, NOT A PALER TINT.
 *
 * The mockup draws the suffix lighter than the numeral, and both slices briefly
 * implemented that as a 40% tint of the derived ink. **design-lead rejected the tint
 * outright rather than re-tuning it** (2026-08-08): measured, it fails for every team
 * at every floor — 2.11:1 red, 1.70:1 gold — so no choice of floor rescues it, and
 * the `%` is load-bearing text, not decoration. The card this replaces showed
 * absolute counts (`60`, `200`), so a bare `64` is ambiguous with exactly the numbers
 * feat-227 removed.
 *
 * Hierarchy comes from size and weight instead (26 px vs 14 px), which cannot fail a
 * contrast check. The alpha constant that encoded the rejected rule is deleted rather
 * than left unused — a constant named for a rule that lost is a false premise waiting
 * for the next reader.
 *
 * Kept as a named function, not inlined, so the suffix's color has one obvious home
 * if it ever diverges again.
 */
export function barPercentSuffixColor(hex: string | undefined | null): string {
  return barTextColor(hex);
}

/** Emoji fallback map — mirrors `_emojiForPlaceholder` in the Flutter widget. */
export function emojiForPlaceholder(key: string | undefined): string {
  switch (key) {
    case 'pepper':
      return '🌶️';
    case 'avocado':
      return '🥑';
    case 'baguette':
      return '🥖';
    case 'mushroom':
      return '🍄';
    default:
      return '🥗';
  }
}
